import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  canManageProducts,
  canManagePurchaseOrders,
  canManageSalesOrders,
  canManageCustomers,
  canManageSuppliers,
  canOperateInventory,
  isAdmin,
} from "@/lib/permissions";

interface CreateAction {
  label: string;
  path: string;
  icon: string;
  gate: (role: string | null | undefined) => boolean;
}

const CREATE_ACTIONS: CreateAction[] = [
  { label: "Product", path: "/products/new", icon: "ri-archive-2-line", gate: canManageProducts },
  { label: "Sales Order", path: "/sales-orders?new=1", icon: "ri-file-list-line", gate: canManageSalesOrders },
  { label: "Purchase Order", path: "/purchase-orders?new=1", icon: "ri-shopping-cart-line", gate: canManagePurchaseOrders },
  { label: "Customer", path: "/customers?new=1", icon: "ri-user-3-line", gate: canManageCustomers },
  { label: "Supplier", path: "/suppliers?new=1", icon: "ri-truck-line", gate: canManageSuppliers },
  { label: "Transfer", path: "/transfers?new=1", icon: "ri-arrow-left-right-line", gate: canOperateInventory },
  { label: "Location", path: "/locations?new=1", icon: "ri-map-pin-line", gate: isAdmin },
];

export function QuickCreate() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const visible = CREATE_ACTIONS.filter((a) => a.gate(role));
  if (visible.length === 0) return null;

  return (
    <div className="relative mr-3" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
      >
        <i className="ri-add-line" aria-hidden="true" />
        <span className="hidden sm:inline">New</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl border border-slate-200 py-2 z-50 shadow-lg"
          role="menu"
          aria-label="Create new"
        >
          <p className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Create new</p>
          {visible.map((a) => (
            <button
              key={a.path}
              onClick={() => {
                setOpen(false);
                navigate(a.path);
              }}
              role="menuitem"
              className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3 whitespace-nowrap"
            >
              <div className="w-6 h-6 flex items-center justify-center shrink-0 text-slate-500" aria-hidden="true">
                <i className={a.icon} />
              </div>
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}